import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  CustomPage,
  CustomPageIdInput,
  CustomPageInput,
  CustomPagePreviewDraftInput,
  CustomPageSummary,
} from './custom-pages.types'

export interface CustomPageOrganizationScope {
  organizationId: OrganizationId
}

/** The show query key's entries; the shared key builder can't mark an entry required. */
export interface CustomPageShowKeyEntries extends CustomPageOrganizationScope {
  customPageId?: CustomPageIdInput
}

/** Calls that read or change one page. */
interface CustomPageTarget extends CustomPageOrganizationScope {
  customPageId: CustomPageIdInput
}

/** Query functions read only the key. */
type CustomPageQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'>

/** Every page of the organization, ordered by title. */
export function getCustomPages({
  queryKey: [{ organizationId }],
}: CustomPageQueryContext<CustomPageOrganizationScope>) {
  return axios.get<CustomPageSummary[]>(
    '/custom-pages',
    buildAdminRequestConfig({ organizationId })
  )
}

export function getCustomPage({
  queryKey: [{ organizationId, customPageId }],
}: CustomPageQueryContext<CustomPageShowKeyEntries>) {
  return axios.get<CustomPage>(
    `/custom-pages/${customPageId}`,
    buildAdminRequestConfig({ organizationId })
  )
}

export function createCustomPage({
  organizationId,
  values,
}: CustomPageOrganizationScope & { values: CustomPageInput }) {
  return axios.post<CustomPage>(
    '/custom-pages',
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

/** Replaces every field of the page. */
export function updateCustomPage({
  organizationId,
  customPageId,
  values,
}: CustomPageTarget & { values: CustomPageInput }) {
  return axios.put<CustomPage>(
    `/custom-pages/${customPageId}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export function deleteCustomPage({ organizationId, customPageId }: CustomPageTarget) {
  return axios.delete<void>(
    `/custom-pages/${customPageId}`,
    buildAdminRequestConfig({ organizationId })
  )
}

/** 204 No Content. Shows unsaved HTML in the page's storefront preview without saving it. */
export function saveCustomPagePreviewDraft({
  organizationId,
  customPageId,
  values,
}: CustomPageTarget & { values: CustomPagePreviewDraftInput }) {
  return axios.put<string>(
    `/custom-pages/${customPageId}/preview-draft`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}
